import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageCircle, MapPin, Clock, Send, HelpCircle } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
interface ContactPageProps {
  onNavigate: (page: string) => void;
}
export function ContactPage({
  onNavigate
}: ContactPageProps) {
  const {
    success
  } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'support',
    message: ''
  });
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      success('Сообщение успешно отправлено! Мы ответим вам в ближайшее время.');
      setFormData({
        name: '',
        email: '',
        subject: 'support',
        message: ''
      });
    }, 1500);
  };
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-23502">
      <div className="bg-white border-b border-slate-200 pt-6 pb-12" data-id="element-23503">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23504">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Контакты'
        }]} className="mb-8" data-id="element-23505" />
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" data-id="element-23506">
            Свяжитесь с нами
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl" data-id="element-23507">
            У вас есть вопросы, предложения или нужна помощь? Наша команда
            всегда готова помочь.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-23508">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-23509">
          {/* Contact Info */}
          <div className="lg:col-span-5 space-y-8" data-id="element-23510">
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm" data-id="element-23511">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-23512">
                Контактная информация
              </h2>

              <div className="space-y-6" data-id="element-23513">
                <div className="flex items-start gap-4" data-id="element-23514">
                  <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0" data-id="element-23515">
                    <Mail className="w-5 h-5 text-indigo-600" data-id="element-23516" />
                  </div>
                  <div data-id="element-23517">
                    <div className="text-sm font-medium text-slate-500 mb-1" data-id="element-23518">
                      Email поддержки
                    </div>
                    <a href="mailto:support@astriqo.com" className="text-slate-900 font-medium hover:text-indigo-600 transition-colors" data-id="element-23519">
                      support@astriqo.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-23520">
                  <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center flex-shrink-0" data-id="element-23521">
                    <MessageCircle className="w-5 h-5 text-sky-600" data-id="element-23522" />
                  </div>
                  <div data-id="element-23523">
                    <div className="text-sm font-medium text-slate-500 mb-1" data-id="element-23524">
                      Telegram
                    </div>
                    <a href="#" className="text-slate-900 font-medium hover:text-sky-600 transition-colors" data-id="element-23525">
                      @astriqo_support
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-23526">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0" data-id="element-23527">
                    <Clock className="w-5 h-5 text-emerald-600" data-id="element-23528" />
                  </div>
                  <div data-id="element-23529">
                    <div className="text-sm font-medium text-slate-500 mb-1" data-id="element-23530">
                      Режим работы
                    </div>
                    <div className="text-slate-900 font-medium" data-id="element-23531">
                      Ежедневно, 10:00 - 20:00 (МСК)
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 rounded-2xl p-8 border border-indigo-100" data-id="element-23532">
              <div className="flex items-center gap-3 mb-4" data-id="element-23533">
                <HelpCircle className="w-6 h-6 text-indigo-600" data-id="element-23534" />
                <h2 className="text-lg font-bold text-indigo-900" data-id="element-23535">
                  Частые вопросы
                </h2>
              </div>
              <p className="text-indigo-800/80 mb-6 text-sm leading-relaxed" data-id="element-23536">
                Возможно, мы уже ответили на ваш вопрос. Посетите раздел FAQ,
                чтобы найти информацию о возвратах, отчетах и консультациях.
              </p>
              <Button variant="outline" className="w-full bg-white border-indigo-200 text-indigo-700 hover:bg-indigo-100" onClick={() => onNavigate('faq')} data-id="element-23537">
                Перейти в FAQ
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7" data-id="element-23538">
            <div className="bg-white rounded-2xl p-8 sm:p-10 border border-slate-200 shadow-xl" data-id="element-23539">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-23540">
                Написать нам
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-23541">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-23542">
                  <div className="space-y-2" data-id="element-23543">
                    <label className="text-sm font-medium text-slate-700" data-id="element-23544">
                      Ваше имя
                    </label>
                    <Input placeholder="Иван Иванов" required value={formData.name} onChange={(e: any) => handleChange('name', e.target ? e.target.value : e)} data-id="element-23545" />
                  </div>
                  <div className="space-y-2" data-id="element-23546">
                    <label className="text-sm font-medium text-slate-700" data-id="element-23547">
                      Email
                    </label>
                    <Input type="email" placeholder="ivan@example.com" required value={formData.email} onChange={(e: any) => handleChange('email', e.target ? e.target.value : e)} data-id="element-23548" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-23549">
                  <label className="text-sm font-medium text-slate-700" data-id="element-23550">
                    Тема обращения
                  </label>
                  <select className="w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all" value={formData.subject} onChange={e => handleChange('subject', e.target.value)} data-id="element-23551">
                    <option value="support" data-id="element-23552">Техническая поддержка</option>
                    <option value="report" data-id="element-23553">Вопрос по отчету</option>
                    <option value="consultation" data-id="element-23554">Вопрос по консультации</option>
                    <option value="partnership" data-id="element-23555">Сотрудничество</option>
                    <option value="other" data-id="element-23556">Другое</option>
                  </select>
                </div>

                <div className="space-y-2" data-id="element-23557">
                  <label className="text-sm font-medium text-slate-700" data-id="element-23558">
                    Сообщение
                  </label>
                  <textarea className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all min-h-[150px] resize-y" placeholder="Опишите вашу проблему или вопрос максимально подробно..." required value={formData.message} onChange={e => handleChange('message', e.target.value)} data-id="element-23559"></textarea>
                </div>

                <label className="flex items-start gap-2.5 cursor-pointer group" data-id="element-23560">
                  <input type="checkbox" required className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-23561" />
                  <span className="text-xs text-slate-500 leading-relaxed group-hover:text-slate-700 transition-colors" data-id="element-23562">
                    Я соглашаюсь с{' '}
                    <button type="button" onClick={() => onNavigate('public-offer')} className="text-indigo-600 hover:underline" data-id="element-23563">
                      публичной офертой
                    </button>{' '}
                    и{' '}
                    <button type="button" onClick={() => onNavigate('privacy')} className="text-indigo-600 hover:underline" data-id="element-23564">
                      политикой конфиденциальности
                    </button>
                  </span>
                </label>

                <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={isSubmitting} data-id="element-23565">
                  {isSubmitting ? 'Отправка...' : <>
                      <Send className="w-4 h-4 mr-2" data-id="element-23566" />
                      Отправить сообщение
                    </>}
                </Button>

                <p className="text-xs text-slate-500 mt-4 hidden" data-id="element-23567">
                  Нажимая кнопку «Отправить сообщение», вы соглашаетесь с{' '}
                  <button type="button" onClick={() => onNavigate('privacy')} className="text-indigo-600 hover:underline" data-id="element-23568">
                    Политикой конфиденциальности
                  </button>
                  .
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
}